import {
  Component,
  HostBinding,
  Inject,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { AuthService } from '@project/admin/core/auth.service';
import { DOCUMENT } from '@angular/common';
import { environment } from '@project/admin/environments/environment';
import { Icon } from '@project/admin/shared/icon';
import { NotificationService } from '@devop/ngx-metronic/notification';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnDestroy {
  @HostBinding('class')
  class = 'd-flex flex-column flex-root';

  gsiTheme = 'filled_black';
  loading = false;
  email = new FormControl('', [Validators.required, Validators.email]);
  password = new FormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(100),
  ]);
  form = new FormGroup({
    email: this.email,
    password: this.password,
  });
  untilSub = new Subject();
  status: null | 'not_found' | 'no_internet' | 'unknown' = null;
  scriptElement: HTMLScriptElement;

  get loginOptions() {
    return {
      redirect: this.activatedRoute.snapshot.queryParams['redirect'],
    };
  }

  constructor(
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
    private notifService: NotificationService,
    private ngZone: NgZone
  ) {
    this.scriptElement = this.document.createElement('script');
    this.scriptElement.src = 'https://accounts.google.com/gsi/client';
  }

  ngOnInit(): void {
    const theme =
      document.getElementsByTagName('html')[0].getAttribute('data-theme') ??
      'light';
    this.scriptElement.onload = () => {
      google.accounts.id.initialize({
        client_id: environment.google_client_id,
        callback: ({ credential }) =>
          this.ngZone.run(() => this.loginWithGoogle(credential)),
      });
      const element = this.document.getElementById('gsi');
      if (!element) {
        throw new Error('GSI button element not found');
      }
      google.accounts.id.renderButton(element, {
        type: 'standard',
        theme: theme === 'dark' ? 'filled_black' : 'outline',
        shape: 'rectangular',
        size: 'large',
        text: 'continue_with',
        logo_alignment: 'left',
        locale: 'lt',
      });
      this.onLoginWithGoogle();
    };
    this.document.head.append(this.scriptElement);
    this.toggleClasses(true);
  }

  ngOnDestroy() {
    this.scriptElement.remove();
    this.untilSub.complete();
    this.toggleClasses(false);
  }

  onLoginWithGoogle() {
    google.accounts.id.prompt();
    return false;
  }

  async loginWithGoogle(token: string) {
    try {
      this.loading = true;
      await this.authService.loginWithGoogle(token, this.loginOptions);
    } catch (e: any) {
      if (e.message === 'User not found') {
        this.notifService.add({
          type: 'danger',
          svgIcon: Icon.ShieldLocked,
          title: 'Prisijungimas nepavyko',
          message:
            'Toks vartotojas sistemoje nerastas. Jei nesate klientas, prašome pateikti užklausą.',
        });
      } else {
        this.notifService.add({
          type: 'danger',
          svgIcon: Icon.ExclRound,
          title: 'Klaida prisijungiant',
          message: e.message,
        });
      }
    } finally {
      this.loading = false;
    }
  }

  async onSubmit(evt: MouseEvent) {
    evt.preventDefault();
    this.notifService.clear(null);
    const email = this.email.value;
    const password = this.password.value;
    if (this.form.invalid || !email || !password) {
      this.form.markAllAsTouched();
      return false;
    }
    try {
      this.status = null;
      this.loading = true;
      await this.authService.login(email, password, this.loginOptions);
    } catch (er: any) {
      if (er.message === 'Incorrect username/password') {
        this.status = 'not_found';
      } else if (er.message.endsWith('0 Unknown Error')) {
        this.status = 'no_internet';
      } else {
        this.status = 'unknown';
        console.error(er);
      }
    } finally {
      this.loading = false;
    }
    return true;
  }

  private toggleClasses(bool: boolean) {
    const classes = [
      'app-blank',
      'bgi-size-cover',
      'bgi-position-center',
      'bgi-no-repeat',
    ];
    for (const c of classes) {
      this.document.body.classList.toggle(c, bool);
    }
  }
}
